import { memo, useCallback } from "react";
import parse from "html-react-parser";
import { ModalLink } from "./ComplianceGuide/ModalLink";

type ColumnInfo = {
  label: string;
  field: string;
};

type Props = {
  columns: ColumnInfo[];
  data: any;
  hideTableHeader: boolean;
  showNPOHeader: boolean;
  topic: string;
};

export const Table = memo(
  ({ columns, data, hideTableHeader, showNPOHeader, topic }: Props) => {
    const displayContent = useCallback(
      (fieldName: string, fieldValue: any, fieldRow: any): any => {
        // Citation cells arrive as arrays of { label, url }. Keyed on shape rather than
        // `topic` because Table is memo'd: a topic switch can render the new topic against
        // the previous chart's rows, and these arrays must not reach the string checks below.
        if (Array.isArray(fieldValue)) {
          const items = (fieldValue as { label: string; url: string }[]).filter(
            (c) => c?.label,
          );
          if (items.length === 0) return "";
          return (
            <>
              {items.map((c, i) => (
                <span key={i}>
                  {i > 0 && "; "}
                  {c.url ? (
                    <ModalLink label={c.label} url={c.url} />
                  ) : (
                    <span style={{ color: "var(--bs-body-color)" }}>
                      {c.label}
                    </span>
                  )}
                </span>
              ))}
            </>
          );
        }
        if (
          fieldValue?.toLowerCase() === "true" ||
          fieldValue?.toLowerCase() === "yes" ||
          fieldValue === "1" ||
          fieldValue === "2" ||
          fieldValue?.toLowerCase() === "dma"
        ) {
          return <img src="/images/check-org.gif" alt="check logo" />;
        }
        if (
          fieldValue?.toLowerCase() === "false" ||
          fieldValue?.toLowerCase() === "no" ||
          fieldValue === "0"
        ) {
          return "";
        }
        if (topic === "LegalCommRegSummaries" && fieldName === "citation") {
          const definitionsLabel = fieldRow["definitionsLabel"] ?? "";
          const ruleLabel = fieldRow["ruleLabel"] ?? "";
          return (
            <>
              {fieldRow["definitionsURL"] ? (
                <ModalLink
                  label={definitionsLabel}
                  url={fieldRow["definitionsURL"]}
                  labelName={definitionsLabel ? "Definitions : " : ""}
                />
              ) : (
                definitionsLabel && (
                  <span style={{ color: "var(--bs-body-color)" }}>
                    Definitions : {definitionsLabel}
                  </span>
                )
              )}
              {fieldRow["ruleURL"] ? (
                <>
                  <br />
                  <ModalLink
                    label={ruleLabel}
                    url={fieldRow["ruleURL"]}
                    labelName={ruleLabel ? "Rule : " : ""}
                  />
                </>
              ) : (
                ruleLabel && (
                  <>
                    <br />
                    <span style={{ color: "var(--bs-body-color)" }}>
                      Rule : {ruleLabel}
                    </span>
                  </>
                )
              )}
            </>
          );
        }
        if (topic === "LegalDncSummaries" && fieldName === "citations") {
          const ruleLabel = fieldRow["ruleLabel"] ?? "";
          const implementationLabel = fieldRow["implementationLabel"] ?? "";
          const definitionsLabel = fieldRow["definitionsLabel"] ?? "";
          return (
            <>
              {fieldRow["ruleURL"] && (
                <ModalLink
                  label={ruleLabel}
                  url={fieldRow["ruleURL"]}
                  labelName={ruleLabel ? "Rule : " : ""}
                />
              )}
              {fieldRow["implementationURL"] && (
                <>
                  <br />
                  <ModalLink
                    label={implementationLabel}
                    url={fieldRow["implementationURL"]}
                    labelName={implementationLabel ? "Implementation : " : ""}
                  />
                </>
              )}
              {fieldRow["definitionsURL"] && (
                <>
                  <br />
                  <ModalLink
                    label={definitionsLabel}
                    url={fieldRow["definitionsURL"]}
                    labelName={definitionsLabel ? "Definitions : " : ""}
                  />
                </>
              )}
            </>
          );
        }
        if (
          topic === "LegalNpoExemptionSummaries" &&
          fieldName === "citation"
        ) {
          return (
            <ModalLink label={fieldRow["label"]} url={fieldRow["citation"]} />
          );
        }
        if (topic === "LegalDncEbrPermSummaries" && fieldName === "citations") {
          return (
            <ModalLink label={fieldRow["label"]} url={fieldRow["citations"]} />
          );
        }
        if (
          topic === "LegalCallMonitoringSummaries" &&
          fieldName === "citations"
        ) {
          const definitionsLabel = fieldRow["definitionsLabel"] ?? "";
          const ruleLabel = fieldRow["ruleLabel"] ?? "";
          return (
            <>
              {fieldRow["ruleURL"] ? (
                <ModalLink
                  label={ruleLabel}
                  url={fieldRow["ruleURL"]}
                  labelName={ruleLabel ? "Rule : " : ""}
                />
              ) : (
                <>
                  <span style={{ color: "--var(bs-body-color)" }}>
                    {ruleLabel}
                  </span>
                </>
              )}
              {fieldRow["definitionsURL"] ? (
                <>
                  <br />
                  <ModalLink
                    label={definitionsLabel}
                    url={fieldRow["definitionsURL"]}
                    labelName={definitionsLabel ? "Definitions : " : ""}
                  />
                </>
              ) : (
                <>
                  <br />
                  <span style={{ color: "--var(bs-body-color)" }}>
                    {definitionsLabel}
                  </span>
                </>
              )}
            </>
          );
        }
        if (topic === "LegalAdadSummaries" && fieldName === "citations") {
          const definitionsLabel = fieldRow["definitionsLabel"] ?? "";
          const ruleLabel = fieldRow["ruleLabel"] ?? "";
          return (
            <>
              {fieldRow["ruleURL"] && (
                <ModalLink
                  label={ruleLabel}
                  url={fieldRow["ruleURL"]}
                  labelName={ruleLabel ? "Rule : " : ""}
                />
              )}
              {fieldRow["definitionsURL"] && (
                <>
                  <br />
                  <ModalLink
                    label={definitionsLabel}
                    url={fieldRow["definitionsURL"]}
                    labelName={definitionsLabel ? "Definitions : " : ""}
                  />
                </>
              )}
            </>
          );
        }
        if (
          topic === "LegalTimeRestrictionSummaries" ||
          topic === "LegalTimeRestrictionSummariesV2"
        ) {
          if (fieldName === "weekday" && fieldValue === null) {
            return "No state time restrictions; default to federal law.";
          }
        }
        // Registration Bond may carry free-text notes with line breaks
        // ("$25,000 for sellers ...\n$50,000 ...\n$75,000 ..."); preserve them.
        if (topic === "LegalRegFeeBondSummaries" && fieldName === "regBond") {
          return (
            <span style={{ whiteSpace: "pre-line" }}>{fieldValue}</span>
          );
        }
        return parse(fieldValue ?? "");
      },
      [topic],
    );
    const handleHeaderWidth = useCallback(
      (label: string): string | number | undefined => {
        if (label?.toLowerCase() === "jurisdiction") {
          return 160;
        }
        if (label?.toLocaleLowerCase() === "citation") {
          return 250;
        }
        return "auto";
      },
      [],
    );
    const tableDisplay =
      data.length > 0 ? (
        <table className="table table-bordered table-hover table-striped border-primary">
          {!hideTableHeader && (
            <thead>
              {showNPOHeader && (
                <tr>
                  <th>Jurisdiction</th>
                  <th colSpan={2}>Calls with solicitation for donation</th>
                  <th colSpan={2}>
                    Calls with solicitation for a sale of good/service
                  </th>
                  <th>Citation</th>
                </tr>
              )}
              <tr>
                {columns.map((col, index) => (
                  <th
                    key={index}
                    style={{ width: handleHeaderWidth(col.label) }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {data.map((row: any, index: any) => {
              const isTimeRestriction =
                topic === "LegalTimeRestrictionSummaries" ||
                topic === "LegalTimeRestrictionSummariesV2";
              const allTimeFieldsNull =
                isTimeRestriction &&
                row.weekday === null &&
                row.saturday === null &&
                row.sunday === null;
              const timeColumns = isTimeRestriction
                ? ["weekday", "saturday", "sunday", "holiday"]
                : [];

              return (
                <tr key={index}>
                  {columns.map((col, colIndex) => {
                    if (allTimeFieldsNull && timeColumns.includes(col.field)) {
                      if (col.field === "weekday") {
                        const spanCount = row.holiday === null ? 4 : 3;
                        return (
                          <td key={colIndex} colSpan={spanCount}>
                            No state time restrictions; default to federal law.
                          </td>
                        );
                      }
                      if (col.field === "holiday" && row.holiday === null) {
                        return null;
                      }
                      return null;
                    }
                    return (
                      <td key={colIndex}>
                        {displayContent(col.field, row[col.field], row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <h5>{"No records found."}</h5>
      );
    return tableDisplay;
  },
);
